import React, { useState, useEffect, useCallback, useRef } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { verseService } from '../../../services/supabaseService';
import { supabase } from '../../../lib/supabase';

/**
 * WordOrderMode — unscramble the words of a verse by tapping/clicking them.
 * Mobile-first: large tap targets, wrapping word chips.
 */

/** Strip paragraph markers (¶ § etc.) and all punctuation, then normalise whitespace */
const cleanVerseText = (text) => {
  if (!text) return '';
  return (
    text?.replace(/[¶§]/g, '')?.replace(/[^\w\s]/g, '')?.replace(/\s+/g, ' ')?.trim()
  );
};

/**
 * Check whether a user-ordered array of words matches the correct token array,
 * allowing case-insensitive duplicates to occupy any matching position.
 */
const answersMatch = (userWords, correctWords) => {
  if (userWords?.length !== correctWords?.length) return false;
  const correctFreq = {};
  for (const w of correctWords) {
    const key = w?.toLowerCase();
    correctFreq[key] = (correctFreq?.[key] || 0) + 1;
  }
  const userFreq = {};
  for (const w of userWords) {
    const key = w?.toLowerCase();
    userFreq[key] = (userFreq?.[key] || 0) + 1;
  }
  for (const key of Object.keys(correctFreq)) {
    if (correctFreq?.[key] !== userFreq?.[key]) return false;
  }
  for (const key of Object.keys(userFreq)) {
    if ((correctFreq?.[key] || 0) !== userFreq?.[key]) return false;
  }
  const multiWord = new Set(
    Object.keys(correctFreq).filter(k => correctFreq[k] > 1)
  );
  for (let i = 0; i < correctWords?.length; i++) {
    const ck = correctWords?.[i]?.toLowerCase();
    const uk = userWords?.[i]?.toLowerCase();
    if (multiWord?.has(ck) || multiWord?.has(uk)) continue;
    if (ck !== uk) return false;
  }
  return true;
};

/** Play a pleasant bell/chime sound using Web Audio API */
const playBell = () => {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const frequencies = [523.25, 659.25, 783.99, 1046.5]; // C5 E5 G5 C6
    frequencies?.forEach((freq, i) => {
      const osc = ctx?.createOscillator();
      const gain = ctx?.createGain();
      osc?.connect(gain);
      gain?.connect(ctx?.destination);
      osc.type = 'sine';
      osc.frequency.value = freq;
      const startTime = ctx?.currentTime + i * 0.18;
      gain?.gain?.setValueAtTime(0, startTime);
      gain?.gain?.linearRampToValueAtTime(0.35, startTime + 0.02);
      gain?.gain?.exponentialRampToValueAtTime(0.001, startTime + 1.2);
      osc?.start(startTime);
      osc?.stop(startTime + 1.2);
    });
  } catch (e) {
    // Audio not available — silently ignore
  }
};

const WordOrderMode = ({ verse, onComplete, onNext, verseIndex = 0, totalVerses = 1, textSizePx = '16px' }) => {
  const [bank, setBank] = useState([]);
  const [answer, setAnswer] = useState([]);
  const [status, setStatus] = useState('idle'); // idle | correct | incorrect
  const [showAnswer, setShowAnswer] = useState(false);
  const [attempts, setAttempts] = useState(0);

  // Assistant mode state
  const [assistantMode, setAssistantMode] = useState(true);
  // Per-word flash feedback: { [itemId]: 'correct' | 'incorrect' | null }
  const [wordFlash, setWordFlash] = useState({});
  const flashTimers = useRef({});

  // Hint state
  const [hintCount, setHintCount] = useState(0);

  // Add to library state
  const [userId, setUserId] = useState(null);
  const [libraryStatus, setLibraryStatus] = useState('idle'); // idle | checking | already_added | adding | added | error
  const [libraryError, setLibraryError] = useState('');

  // Tokenise cleaned verse text
  const cleanedText = cleanVerseText(verse?.text);
  const tokens = cleanedText ? cleanedText?.split(/\s+/)?.filter(Boolean) : [];

  const shuffle = useCallback((arr) => {
    const a = arr?.map((word, i) => ({ word, id: i }));
    for (let i = a?.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [a[i], a[j]] = [a?.[j], a?.[i]];
    }
    return a;
  }, []);

  // Get current user on mount
  useEffect(() => {
    supabase?.auth?.getSession()?.then(({ data }) => {
      setUserId(data?.session?.user?.id || null);
    });
  }, []);

  // Reset whenever verse changes
  useEffect(() => {
    setBank(shuffle(tokens));
    setAnswer([]);
    setStatus('idle');
    setShowAnswer(false);
    setAttempts(0);
    setWordFlash({});
    setHintCount(0);
    setLibraryStatus('idle');
    setLibraryError('');
    // Clear any pending flash timers
    Object.values(flashTimers?.current)?.forEach(clearTimeout);
    flashTimers.current = {};
  }, [verse?.id, verse?.text]);

  // When verse is completed, check if it's already in the library
  useEffect(() => {
    if (status === 'correct' && userId && verse?.reference) {
      checkIfInLibrary();
    }
  }, [status, userId, verse?.reference]);

  // Cleanup timers on unmount
  useEffect(() => {
    return () => {
      Object.values(flashTimers?.current)?.forEach(clearTimeout);
    };
  }, []);

  const checkIfInLibrary = async () => {
    if (!userId || !verse?.reference) return;
    setLibraryStatus('checking');
    try {
      const { data } = await supabase
        ?.from('verses')
        ?.select('id')
        ?.eq('user_id', userId)
        ?.eq('reference', verse?.reference)
        ?.maybeSingle();
      setLibraryStatus(data ? 'already_added' : 'idle');
    } catch {
      setLibraryStatus('idle');
    }
  };

  const handleAddToLibrary = async () => {
    if (!userId || !verse?.reference) return;
    setLibraryStatus('adding');
    setLibraryError('');
    try {
      const { error } = await verseService?.createVerseFromBible(userId, verse?.reference);
      if (error) {
        if (error?.message?.includes('already')) {
          setLibraryStatus('already_added');
        } else {
          setLibraryError(error?.message || 'Failed to add verse');
          setLibraryStatus('error');
        }
      } else {
        setLibraryStatus('added');
      }
    } catch (err) {
      setLibraryError(err?.message || 'Failed to add verse');
      setLibraryStatus('error');
    }
  };

  /** Flash a word chip with a result colour then clear it */
  const triggerFlash = (itemId, result) => {
    setWordFlash(prev => ({ ...prev, [itemId]: result }));
    if (flashTimers?.current?.[itemId]) clearTimeout(flashTimers?.current?.[itemId]);
    flashTimers.current[itemId] = setTimeout(() => {
      setWordFlash(prev => {
        const next = { ...prev };
        delete next?.[itemId];
        return next;
      });
    }, 700);
  };

  /** Place 2 random words from the bank into their correct positions */
  const handleHint = () => {
    if (status === 'correct') return;

    // Find words in the bank that can be placed in the next available correct positions
    // We need to find positions in tokens[] that are not yet filled in answer[]
    const currentAnswerLength = answer?.length;
    
    // Find all bank items that match their correct position
    // We look for positions starting from currentAnswerLength onward
    const availablePositions = [];
    for (let i = currentAnswerLength; i < tokens?.length; i++) {
      const expectedWord = tokens?.[i]?.toLowerCase();
      const bankItem = bank?.find(b => b?.word?.toLowerCase() === expectedWord);
      if (bankItem) {
        availablePositions?.push({ position: i, bankItem });
      }
    }

    // Shuffle available positions and pick up to 2
    const shuffled = [...availablePositions]?.sort(() => Math.random() - 0.5);
    const toPlace = shuffled?.slice(0, 2);

    if (toPlace?.length === 0) return;

    // Sort by position so we place in order
    toPlace?.sort((a, b) => a?.position - b?.position);

    let newAnswer = [...answer];
    let newBank = [...bank];

    for (const { bankItem } of toPlace) {
      // Find the next correct position for this word
      const nextIdx = newAnswer?.length;
      const expectedWord = tokens?.[nextIdx]?.toLowerCase();
      
      // Find a bank item matching the expected word
      const matchIdx = newBank?.findIndex(b => b?.word?.toLowerCase() === expectedWord);
      if (matchIdx === -1) break;
      
      const item = newBank?.[matchIdx];
      newAnswer = [...newAnswer, item];
      newBank = newBank?.filter((_, i) => i !== matchIdx);
      
      // Flash green
      triggerFlash(item?.id, 'correct');
    }

    setAnswer(newAnswer);
    setBank(newBank);
    setHintCount(prev => prev + 1);

    // Check if verse is now complete
    if (newAnswer?.length === tokens?.length) {
      setStatus('correct');
      playBell();
      const newAttempts = attempts + 1;
      setAttempts(newAttempts);
      onComplete?.({ correct: true, attempts: newAttempts });
    }
  };

  const pickWord = (item) => {
    if (status === 'correct') return;

    if (assistantMode) {
      // Determine what the next expected word is
      const nextIndex = answer?.length;
      const expectedWord = tokens?.[nextIndex]?.toLowerCase();
      const isCorrect = item?.word?.toLowerCase() === expectedWord;

      triggerFlash(item?.id, isCorrect ? 'correct' : 'incorrect');

      if (!isCorrect) {
        // Wrong word — don't place it, just flash red
        return;
      }

      // Correct word — place it
      let newAnswer = [...answer, item];
      setAnswer(newAnswer);
      setBank(prev => prev?.filter(b => b?.id !== item?.id));
      setStatus('idle');

      // Check if verse is now complete
      if (newAnswer?.length === tokens?.length) {
        setStatus('correct');
        playBell();
        // Auto-complete: notify parent immediately
        const newAttempts = attempts + 1;
        setAttempts(newAttempts);
        onComplete?.({ correct: true, attempts: newAttempts });
      }
    } else {
      // Normal mode — place the word and give positive visual feedback
      triggerFlash(item?.id, 'placed');
      setAnswer(prev => [...prev, item]);
      setBank(prev => prev?.filter(b => b?.id !== item?.id));
      setStatus('idle');
    }
  };

  const removeWord = (item) => {
    if (status === 'correct') return;
    setBank(prev => [...prev, item]);
    setAnswer(prev => prev?.filter(a => a?.id !== item?.id));
    setStatus('idle');
  };

  const checkAnswer = () => {
    const userWords = answer?.map(a => a?.word);
    const isCorrect = answersMatch(userWords, tokens);
    const newAttempts = attempts + 1;
    setAttempts(newAttempts);
    setStatus(isCorrect ? 'correct' : 'incorrect');
    if (isCorrect) {
      playBell();
      onComplete?.({ correct: true, attempts: newAttempts });
    } else {
      // Flash all answer words red briefly
      answer?.forEach(item => triggerFlash(item?.id, 'incorrect-answer'));
    }
  };

  const reset = () => {
    setBank(shuffle(tokens));
    setAnswer([]);
    setStatus('idle');
    setShowAnswer(false);
    setWordFlash({});
  };

  const handleNext = () => {
    if (status !== 'correct') {
      onComplete?.({ correct: status === 'correct', attempts });
    }
    onNext?.();
  };

  const progress = totalVerses > 1 ? ((verseIndex) / totalVerses) * 100 : 0;
  const isLastVerse = verseIndex + 1 >= totalVerses;

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Keyframe animations injected via style tag */}
      <style>{`
        @keyframes wordGlowGreen {
          0%   { box-shadow: 0 0 0 0 rgba(34,197,94,0); transform: scale(1); }
          30%  { box-shadow: 0 0 0 8px rgba(34,197,94,0.45), 0 0 18px 4px rgba(34,197,94,0.3); transform: scale(1.12); }
          70%  { box-shadow: 0 0 0 4px rgba(34,197,94,0.2); transform: scale(1.05); }
          100% { box-shadow: 0 0 0 0 rgba(34,197,94,0); transform: scale(1); }
        }
        @keyframes wordShakeRed {
          0%   { transform: translateX(0) scale(1); box-shadow: 0 0 0 0 rgba(239,68,68,0); }
          15%  { transform: translateX(-6px) scale(0.96); box-shadow: 0 0 0 6px rgba(239,68,68,0.35); }
          30%  { transform: translateX(6px) scale(0.96); }
          45%  { transform: translateX(-4px) scale(0.97); }
          60%  { transform: translateX(4px) scale(0.97); }
          75%  { transform: translateX(-2px) scale(0.99); }
          90%  { transform: translateX(2px) scale(0.99); }
          100% { transform: translateX(0) scale(1); box-shadow: 0 0 0 0 rgba(239,68,68,0); }
        }
        @keyframes wordPlaced {
          0%   { transform: scale(1); }
          40%  { transform: scale(1.08); }
          100% { transform: scale(1); }
        }
        .word-flash-correct  { animation: wordGlowGreen 0.65s ease-out forwards; }
        .word-flash-incorrect { animation: wordShakeRed 0.65s ease-out forwards; }
        .word-flash-placed   { animation: wordPlaced 0.2s ease-out forwards; }
        .word-flash-incorrect-answer { animation: wordShakeRed 0.65s ease-out forwards; }
      `}</style>
      {/* Top bar */}
      <div className="bg-card border-b border-border px-4 py-3 flex items-center gap-3 sticky top-0 z-10">
        <div className="flex-1">
          <div className="flex items-center justify-between mb-1">
            <span className="text-sm font-medium text-foreground">{verse?.reference}</span>
            {totalVerses > 1 && (
              <span className="text-xs text-muted-foreground">{verseIndex + 1} / {totalVerses}</span>
            )}
          </div>
          {totalVerses > 1 && (
            <div className="w-full bg-muted rounded-full h-1.5">
              <div
                className="bg-primary h-1.5 rounded-full transition-all duration-300"
                style={{ width: `${progress}%` }}
              />
            </div>
          )}
        </div>
      </div>
      <div className="flex-1 px-4 py-5 flex flex-col gap-5 max-w-2xl mx-auto w-full">
        {/* Mode label + Assistant Mode toggle */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 text-accent">
            <Icon name="Shuffle" size={18} />
            <span className="text-sm font-semibold uppercase tracking-wide">Word Order</span>
          </div>

          {/* Assistant Mode toggle */}
          <button
            onClick={() => setAssistantMode(v => !v)}
            className={`flex items-center gap-2 px-3 py-1.5 rounded-full border text-xs font-semibold transition-all duration-200 ${
              assistantMode
                ? 'bg-primary/15 border-primary/50 text-primary' :'bg-muted border-border text-muted-foreground hover:border-primary/40 hover:text-primary/70'
            }`}
          >
            <Icon name="Sparkles" size={13} />
            Assistant Mode
            {/* Toggle pill */}
            <span
              className={`inline-flex w-8 h-4 rounded-full transition-colors duration-200 relative ml-1 ${
                assistantMode ? 'bg-primary' : 'bg-muted-foreground/30'
              }`}
            >
              <span
                className={`absolute top-0.5 w-3 h-3 rounded-full bg-white shadow transition-transform duration-200 ${
                  assistantMode ? 'translate-x-4' : 'translate-x-0.5'
                }`}
              />
            </span>
          </button>
        </div>

        {/* Verse reference box — only show reference, not full text */}
        <div className="rounded-xl border-2 border-primary/40 bg-primary/10 px-4 py-3 flex items-center justify-between gap-3 shadow-sm">
          <p className="text-base font-bold text-primary tracking-wide">{verse?.reference}</p>
          {verse?.version && (
            <span className="text-xs font-semibold px-2 py-1 rounded-md bg-primary/15 text-primary/80 border border-primary/25 flex-shrink-0">
              {verse?.version}
            </span>
          )}
        </div>

        {/* Assistant mode hint */}
        {assistantMode && status !== 'correct' && (
          <div className="flex items-center gap-2 text-xs text-primary/80 bg-primary/8 border border-primary/20 rounded-lg px-3 py-2">
            <Icon name="Info" size={13} />
            <span>Tap the next word in sequence — green means correct, red means try another.</span>
          </div>
        )}

        {/* Answer area */}
        <div
          className={`min-h-[100px] rounded-xl border-2 p-4 transition-colors duration-300 ${
            status === 'correct' ? 'border-success bg-success/10'
              : status === 'incorrect' ? 'border-destructive bg-destructive/10' : 'border-border bg-muted/30'
          }`}
        >
          {answer?.length === 0 ? (
            <p className="text-muted-foreground text-sm italic text-center mt-2">
              Tap words below to build the verse…
            </p>
          ) : (
            <div className="flex flex-wrap gap-2">
              {answer?.map((item, idx) => {
                const flash = wordFlash?.[item?.id];
                return (
                  <button
                    key={`ans-${item?.id}-${idx}`}
                    onClick={() => removeWord(item)}
                    disabled={status === 'correct'}
                    className={`px-3 py-2 rounded-lg text-sm font-medium border transition-colors duration-150 active:scale-95
                      ${flash === 'incorrect-answer' ?'bg-destructive/20 border-destructive/50 text-destructive word-flash-incorrect-answer'
                        : status === 'correct' ?'bg-success/20 border-success/40 text-success-foreground cursor-default' :'bg-primary/15 border-primary/40 text-primary hover:bg-destructive/15 hover:border-destructive/40 hover:text-destructive'
                      }`}
                    style={{ fontSize: textSizePx, minHeight: '44px' }}
                  >
                    {item?.word}
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Status feedback */}
        {status === 'correct' && (
          <div className="flex items-center gap-2 text-success bg-success/10 border border-success/30 rounded-lg px-4 py-3">
            <Icon name="CheckCircle" size={20} />
            <span className="font-medium">
              {assistantMode ? '🔔 Perfect! Verse complete!' : 'Correct! Well done.'}
            </span>
          </div>
        )}
        {status === 'incorrect' && !assistantMode && (
          <div className="flex items-center gap-2 text-destructive bg-destructive/10 border border-destructive/30 rounded-lg px-4 py-3">
            <Icon name="XCircle" size={20} />
            <span className="font-medium">Not quite — try again or rearrange the words.</span>
          </div>
        )}

        {/* Add to Library prompt — shown on completion */}
        {status === 'correct' && userId && (
          <div className="rounded-xl border border-border bg-card px-4 py-3 flex items-center justify-between gap-3">
            <div className="flex items-center gap-2 min-w-0">
              <Icon name="BookMarked" size={18} className="text-primary flex-shrink-0" />
              <div className="min-w-0">
                <p className="text-sm font-semibold text-foreground leading-tight">Add to Your Library</p>
                <p className="text-xs text-muted-foreground truncate">{verse?.reference}</p>
              </div>
            </div>

            {libraryStatus === 'checking' && (
              <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                <Icon name="Loader" size={14} className="animate-spin" />
                <span>Checking…</span>
              </div>
            )}

            {libraryStatus === 'idle' && (
              <button
                onClick={handleAddToLibrary}
                className="flex-shrink-0 flex items-center gap-1.5 px-3 py-2 rounded-lg bg-primary text-primary-foreground text-xs font-semibold hover:opacity-90 active:scale-95 transition-all"
              >
                <Icon name="Plus" size={14} />
                Add Verse
              </button>
            )}

            {libraryStatus === 'adding' && (
              <div className="flex items-center gap-1.5 text-xs text-primary">
                <Icon name="Loader" size={14} className="animate-spin" />
                <span>Adding…</span>
              </div>
            )}

            {(libraryStatus === 'added') && (
              <div className="flex items-center gap-1.5 text-xs text-success font-semibold">
                <Icon name="CheckCircle" size={14} />
                <span>Added!</span>
              </div>
            )}

            {libraryStatus === 'already_added' && (
              <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                <Icon name="Check" size={14} />
                <span>In library</span>
              </div>
            )}

            {libraryStatus === 'error' && (
              <div className="flex flex-col items-end gap-1">
                <span className="text-xs text-destructive">{libraryError || 'Error'}</span>
                <button
                  onClick={handleAddToLibrary}
                  className="text-xs text-primary underline"
                >
                  Retry
                </button>
              </div>
            )}
          </div>
        )}

        {/* Word bank */}
        {status !== 'correct' && (
          <div>
            <div className="flex items-center justify-between mb-3">
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                Word Bank
              </p>
              {/* Hint button */}
              <button
                onClick={handleHint}
                disabled={bank?.length <= 3}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-xs font-semibold transition-all duration-200 active:scale-95
                  ${bank?.length <= 3
                    ? 'opacity-40 cursor-not-allowed border-border text-muted-foreground bg-muted'
                    : 'border-amber-400/60 bg-amber-400/10 text-amber-600 hover:bg-amber-400/20 hover:border-amber-400'
                  }`}
                title={bank?.length <= 3 ? 'Hint disabled — almost done!' : 'Place 2 words in the correct position'}
              >
                <Icon name="Lightbulb" size={13} />
                Hint
                {hintCount > 0 && (
                  <span className="ml-0.5 opacity-70">({hintCount})</span>
                )}
              </button>
            </div>
            <div className="flex flex-wrap gap-2">
              {bank?.map((item) => {
                const flash = wordFlash?.[item?.id];
                return (
                  <button
                    key={`bank-${item?.id}`}
                    onClick={() => pickWord(item)}
                    className={`px-3 py-2 rounded-lg text-sm font-medium border shadow-sm
                      transition-colors duration-150
                      ${flash === 'correct' ?'bg-success/25 border-success/60 text-success word-flash-correct'
                        : flash === 'incorrect' ?'bg-destructive/20 border-destructive/50 text-destructive word-flash-incorrect'
                          : flash === 'placed' ?'bg-primary/20 border-primary/50 text-primary word-flash-placed' :'border-border bg-card hover:bg-primary/10 hover:border-primary/50 hover:text-primary active:scale-95'
                      }`}
                    style={{ fontSize: textSizePx, minHeight: '44px' }}
                  >
                    {item?.word}
                  </button>
                );
              })}
              {bank?.length === 0 && status !== 'correct' && (
                <p className="text-xs text-muted-foreground italic">All words placed — check your answer!</p>
              )}
            </div>
          </div>
        )}

        {/* Show answer toggle */}
        {showAnswer && (
          <div className="bg-muted/50 border border-border rounded-xl p-4">
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">Correct Order</p>
            <p className="text-foreground leading-relaxed" style={{ fontSize: textSizePx }}>
              {cleanedText}
            </p>
          </div>
        )}

        {/* Action buttons */}
        <div className="flex flex-wrap gap-3 mt-auto pb-4">
          <Button
            variant="ghost"
            onClick={reset}
            iconName="RotateCcw"
            iconPosition="left"
            className="min-h-[48px] flex-1 sm:flex-none"
          >
            Reset
          </Button>

          {!assistantMode && (
            <Button
              variant="outline"
              onClick={() => setShowAnswer(v => !v)}
              iconName={showAnswer ? 'EyeOff' : 'Eye'}
              iconPosition="left"
              className="min-h-[48px] flex-1 sm:flex-none"
            >
              {showAnswer ? 'Hide Answer' : 'Show Answer'}
            </Button>
          )}

          {status !== 'correct' ? (
            !assistantMode && (
              <Button
                variant="default"
                onClick={checkAnswer}
                disabled={answer?.length !== tokens?.length}
                iconName="Check"
                iconPosition="left"
                className="min-h-[48px] flex-1 sm:flex-none bg-gradient-to-r from-primary to-primary/80"
              >
                Check Order
              </Button>
            )
          ) : (
            <button
              onClick={handleNext}
              className={`min-h-[48px] flex-1 sm:flex-none flex items-center justify-center gap-2 px-5 py-3 rounded-xl font-semibold text-sm transition-all duration-200 active:scale-95 shadow-md
                ${isLastVerse
                  ? 'bg-gradient-to-r from-primary via-primary/90 to-accent text-primary-foreground hover:opacity-90 hover:shadow-primary/30 hover:shadow-lg'
                  : 'bg-gradient-to-r from-primary to-primary/80 text-primary-foreground hover:opacity-90'
                }`}
            >
              {isLastVerse ? (
                <>
                  <Icon name="Trophy" size={16} />
                  Finish
                  <Icon name="ArrowRight" size={16} />
                </>
              ) : (
                <>
                  Next Verse
                  <Icon name="ArrowRight" size={16} />
                </>
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default WordOrderMode;
